<section class="issue-editor">
  <header class="issue-editor__header">
    <div>
      <div class="issue-editor__eyebrow">{{ i18n.t('issueEditor.eyebrow') }}</div>
      <h2 class="issue-editor__title">{{ i18n.t('issueEditor.title') }}</h2>
      <p class="issue-editor__meta">{{ i18n.t('issueEditor.meta') }}</p>
    </div>

    <span class="app-status-chip" [ngClass]="getStatusClass()">
      {{ getStatusLabel() }}
    </span>
  </header>

  <section class="app-panel issue-editor__panel">
    <div class="app-panel__header">
      <h3 class="app-panel__title">{{ i18n.t('issueEditor.sections.details') }}</h3>
    </div>

    <form id="issue-editor-form" class="issue-editor__form" [formGroup]="form" (ngSubmit)="save()">
      <label
        class="app-field issue-editor__field-title"
        [class.app-field--invalid]="form.controls.title.invalid && form.controls.title.touched"
      >
        <span class="app-field__label">{{ i18n.t('issueReport.fields.title') }}</span>
        <input type="text" formControlName="title" maxlength="255" />
        @if (form.controls.title.touched && form.controls.title.hasError('required')) {
          <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
        } @else if (form.controls.title.touched && form.controls.title.hasError('maxlength')) {
          <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.titleTooLong') }}</span>
        }
      </label>

      <label class="app-field">
        <span class="app-field__label">{{ i18n.t('issueReport.fields.priority') }}</span>
        <select formControlName="priority">
          @for (priority of priorities; track priority.value) {
            <option [value]="priority.value">{{ i18n.t(priority.labelKey) }}</option>
          }
        </select>
      </label>

      <label
        class="app-field issue-editor__field-full"
        [class.app-field--invalid]="
          form.controls.description.invalid && form.controls.description.touched
        "
      >
        <span class="app-field__label">{{ i18n.t('issueReport.fields.description') }}</span>
        <textarea class="app-textarea" formControlName="description" rows="5"></textarea>
        @if (form.controls.description.touched && form.controls.description.hasError('required')) {
          <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
        }
      </label>

      <label
        class="app-field"
        [class.app-field--invalid]="
          form.controls.locationId.invalid && form.controls.locationId.touched
        "
      >
        <span class="app-field__label">{{ i18n.t('issueReport.fields.location') }}</span>
        <select formControlName="locationId" [disabled]="isLoadingOptions()">
          @for (location of locations(); track location.id) {
            <option [value]="location.id">{{ location.name }}</option>
          }
        </select>
        @if (form.controls.locationId.touched && form.controls.locationId.hasError('required')) {
          <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
        }
      </label>

      <label
        class="app-field"
        [class.app-field--invalid]="
          form.controls.assignedRoleId.invalid && form.controls.assignedRoleId.touched
        "
      >
        <span class="app-field__label">{{ i18n.t('issueReport.fields.assignedRole') }}</span>
        <select formControlName="assignedRoleId" [disabled]="isLoadingOptions()">
          @for (role of roles(); track role.id) {
            <option [value]="role.id">{{ role.name }}</option>
          }
        </select>
        @if (
          form.controls.assignedRoleId.touched && form.controls.assignedRoleId.hasError('required')
        ) {
          <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
        }
      </label>

      <label class="app-field">
        <span class="app-field__label">{{ i18n.t('issueReport.fields.dueDate') }}</span>
        <input type="datetime-local" formControlName="dueDate" />
      </label>

      <div class="issue-editor__readonly-field">
        <span>{{ i18n.t('issueEditor.fields.status') }}</span>
        <span class="app-status-chip" [ngClass]="getStatusClass()">{{ getStatusLabel() }}</span>
      </div>
    </form>

    @if (isLoadingOptions()) {
      <div class="app-empty-state"><span class="app-message__icon app-message__icon--loading fa-solid fa-spinner fa-spin" aria-hidden="true"></span> {{ i18n.t('issueEditor.states.loadingOptions') }}</div>
    } @else if (optionsLoadError(); as error) {
      <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
    }
  </section>

  <section class="app-panel issue-editor__panel">
    <div class="app-panel__header">
      <h3 class="app-panel__title">{{ i18n.t('issueEditor.sections.audit') }}</h3>
    </div>

    <dl class="issue-editor__audit-details">
      <div>
        <dt>{{ i18n.t('issueEditor.fields.reporter') }}</dt>
        <dd>{{ reporterName() || '—' }}</dd>
      </div>
      <div>
        <dt>{{ i18n.t('issueEditor.fields.createdAt') }}</dt>
        <dd>{{ displayedIssue().created_at | date: 'yyyy-MM-dd HH:mm' }}</dd>
      </div>
      <div>
        <dt>{{ i18n.t('issueEditor.fields.updatedAt') }}</dt>
        <dd>{{ displayedIssue().updated_at | date: 'yyyy-MM-dd HH:mm' }}</dd>
      </div>
      <div>
        <dt>{{ i18n.t('issueEditor.fields.reason') }}</dt>
        <dd>{{ displayedIssue().reason || i18n.t('issueEditor.fallbacks.noReason') }}</dd>
      </div>
      <div>
        <dt>{{ i18n.t('issueEditor.fields.startedAt') }}</dt>
        <dd>
          @if (displayedIssue().issue_start_date; as startedAt) {
            {{ startedAt | date: 'yyyy-MM-dd HH:mm' }}
          } @else {
            {{ i18n.t('issueEditor.fallbacks.noDate') }}
          }
        </dd>
      </div>
      <div>
        <dt>{{ i18n.t('issueEditor.fields.endedAt') }}</dt>
        <dd>
          @if (displayedIssue().issue_end_date; as endedAt) {
            {{ endedAt | date: 'yyyy-MM-dd HH:mm' }}
          } @else {
            {{ i18n.t('issueEditor.fallbacks.noDate') }}
          }
        </dd>
      </div>
    </dl>

    <section class="app-panel app-panel--nested issue-editor__attachments">
      <div class="app-panel__header issue-editor__attachments-header">
        <div>
          <h4 class="app-panel__title">{{ i18n.t('issueEditor.fields.attachments') }}</h4>
          <p class="app-panel__meta">{{ i18n.t('issueEditor.attachments.hint') }}</p>
        </div>

        <label
          class="secondary-button secondary-button--compact secondary-button--indigo app-attachment-upload issue-editor__upload"
          [class.app-attachment-upload--disabled]="isSaving()"
        >
          <span class="fa-solid fa-camera" aria-hidden="true"></span>
          {{ i18n.t('issueEditor.attachments.add') }}
          <input
            type="file"
            accept="image/*"
            multiple
            [disabled]="isSaving()"
            (change)="addAttachments($event)"
          />
        </label>
      </div>

      @if (existingAttachments().length || newAttachments().length) {
        <ul class="app-attachment-list issue-editor__attachment-list">
          @for (attachment of existingAttachments(); track attachment.id) {
            <li class="app-attachment-item issue-editor__attachment">
              <span class="fa-solid fa-image" aria-hidden="true"></span>
              <span class="app-attachment-name issue-editor__attachment-name">{{
                attachment.original_name
              }}</span>
              <button
                class="app-icon-button app-attachment-remove issue-editor__remove-attachment"
                type="button"
                [disabled]="isSaving()"
                [attr.aria-label]="
                  i18n.t('issueEditor.attachments.remove') + ': ' + attachment.original_name
                "
                (click)="removeExistingAttachment(attachment)"
              >
                <span class="fa-solid fa-trash" aria-hidden="true"></span>
              </button>
            </li>
          }
          @for (attachment of newAttachments(); track $index) {
            <li class="app-attachment-item issue-editor__attachment">
              <span class="fa-solid fa-image" aria-hidden="true"></span>
              <span class="app-attachment-name issue-editor__attachment-name">
                {{ attachment.name }}
                <small>{{ i18n.t('issueEditor.attachments.new') }}</small>
              </span>
              <button
                class="app-icon-button app-attachment-remove issue-editor__remove-attachment"
                type="button"
                [disabled]="isSaving()"
                [attr.aria-label]="
                  i18n.t('issueEditor.attachments.remove') + ': ' + attachment.name
                "
                (click)="removeNewAttachment($index)"
              >
                <span class="fa-solid fa-trash" aria-hidden="true"></span>
              </button>
            </li>
          }
        </ul>
      } @else {
        <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('issueEditor.attachments.empty') }}</div>
      }

      @if (attachmentError(); as error) {
        <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }
    </section>
  </section>

  @if (saveError(); as error) {
    <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
  }

  <footer class="issue-editor__actions">
    <div class="issue-editor__actions-left">
      <button
        class="secondary-button issue-editor__decision-action"
        type="button"
        [disabled]="isSaving()"
        (click)="openDecision()"
      >
        <span class="fa-solid fa-scale-balanced" aria-hidden="true"></span>
        {{ i18n.t('issueEditor.actions.decision') }}
      </button>
    </div>

    <div class="issue-editor__actions-right">
      <button
        class="secondary-button secondary-button--indigo"
        type="button"
        [disabled]="isSaving()"
        (click)="cancel()"
      >
        {{ i18n.t('issueEditor.actions.cancel') }}
      </button>
      <button
        class="primary-button"
        type="submit"
        form="issue-editor-form"
        [disabled]="isSaving() || isLoadingOptions() || !!optionsLoadError()"
      >
        {{ isSaving() ? i18n.t('issueEditor.actions.saving') : i18n.t('issueEditor.actions.save') }}
      </button>
    </div>
  </footer>
</section>

@if (isDecisionOpen()) {
  <app-issue-decision-modal
    [issue]="displayedIssue()"
    (closed)="closeDecision()"
    (decided)="handleDecision($event)"
  />
}
